import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useBackups, type BackupRow } from "@/lib/queries";
import { useDeleteBackups } from "@/lib/mutations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DatabaseBackup, Trash, TriangleAlert } from "lucide-react";
import { fmtDateTime } from "@emr/shared/india";

/** Human-readable size: MB with one decimal, GB with two, B/KB for tiny seeds. */
function fmtSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function tierBadgeVariant(tier: string): "default" | "secondary" | "outline" {
  if (tier === "quarterly") return "default";
  if (tier === "monthly") return "secondary";
  return "outline";
}

export default function BackupsPage() {
  const { isAdmin } = useAuth();
  const { data, isLoading, error } = useBackups(isAdmin);
  const deleteBackups = useDeleteBackups();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmKeys, setConfirmKeys] = useState<string[] | null>(null);

  const rows = useMemo(() => data?.backups ?? [], [data]);

  // The newest backup (first row — server sorts newest-first). Deleting it
  // breaks the slot-config apply wizard's 45-min freshness gate until a new
  // backup runs, so the confirm dialog warns explicitly.
  const newestKey = rows.length > 0 ? rows[0].key : null;
  const includesNewest = confirmKeys !== null && newestKey !== null && confirmKeys.includes(newestKey);
  const confirmBytes = useMemo(() => {
    if (confirmKeys === null) return 0;
    return rows.filter((r) => confirmKeys.includes(r.key)).reduce((sum, r) => sum + r.sizeBytes, 0);
  }, [confirmKeys, rows]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.key)));
  };

  const doDelete = () => {
    if (confirmKeys === null || confirmKeys.length === 0) return;
    deleteBackups.mutate(confirmKeys, { onSettled: () => { setSelected(new Set()); setConfirmKeys(null); } });
  };

  // Direct-URL guard (settings.tsx pattern): the sidebar only renders the
  // link for admins, but the route is reachable by URL — render a clear
  // forbidden state; no gated fetch fires for non-admins.
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Backups</h1>
        <p className="mt-4 text-sm text-muted">You do not have permission to manage backups.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500 flex items-center gap-2">
          <DatabaseBackup size={20} /> Backups
        </h1>
        <div className="flex items-center gap-3">
          <div className="text-xs text-muted">
            {rows.length} backup{rows.length === 1 ? "" : "s"} · {fmtSize(rows.reduce((s, r) => s + r.sizeBytes, 0))}
          </div>
          <Button
            variant="destructive"
            size="sm"
            disabled={selected.size === 0 || deleteBackups.isPending}
            onClick={() => setConfirmKeys([...selected])}
          >
            <Trash size={14} className="mr-1" /> Delete selected ({selected.size})
          </Button>
        </div>
      </div>

      {isLoading && (
        <Card><CardContent className="p-4 space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-2/3" />
        </CardContent></Card>
      )}

      {!isLoading && error && (
        <Alert variant="destructive">
          <AlertTitle>Backup worker endpoints not available</AlertTitle>
          <AlertDescription>
            The backup worker could not be reached or its list endpoint is not deployed yet — deploy the
            emr-backup Worker to enable listing. Retrying will not help until then.
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <Card><CardContent className="p-8 text-center text-sm text-muted">No backups found.</CardContent></Card>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                  </TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Dumped at</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r: BackupRow) => (
                  <TableRow key={r.key}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(r.key)}
                        onCheckedChange={() => toggle(r.key)}
                        aria-label={`Select backup ${r.date} ${r.tier}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{r.date}</TableCell>
                    <TableCell><Badge variant={tierBadgeVariant(r.tier)}>{r.tier}</Badge></TableCell>
                    <TableCell>{fmtSize(r.sizeBytes)}</TableCell>
                    <TableCell className="text-muted">{r.dumpedAt ? fmtDateTime(r.dumpedAt) : "—"}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        disabled={deleteBackups.isPending}
                        onClick={() => setConfirmKeys([r.key])}
                        aria-label={`Delete backup ${r.date} ${r.tier}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Confirm dialog — destructive, irreversible (R2 delete). Warns
          explicitly when the selection includes the NEWEST backup, which the
          slot-config apply wizard's 45-min freshness gate depends on. */}
      <Dialog open={confirmKeys !== null} onOpenChange={(open) => { if (!open) setConfirmKeys(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {confirmKeys?.length ?? 0} backup{(confirmKeys?.length ?? 0) === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Delete {confirmKeys?.length ?? 0} backup{(confirmKeys?.length ?? 0) === 1 ? "" : "s"} freeing ~{fmtSize(confirmBytes)}? This cannot be undone.
                </p>
                {includesNewest && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
                    <TriangleAlert size={16} className="mt-0.5 shrink-0" />
                    <span>
                      The most recent backup is required by the slot-config apply wizard's freshness gate — deleting it
                      may block applying until a new backup runs.
                    </span>
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmKeys(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={deleteBackups.isPending}>
              {deleteBackups.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
